/**
 * Pure, dependency-free learning-stats logic.
 *
 * This is the single source of truth for streak math and certificate
 * eligibility. It is intentionally free of Convex, React and storage concerns so
 * it can be:
 *   - unit tested in isolation (see `learning-stats.test.ts`),
 *   - run on the Convex server (the stats query feeds it server rows),
 *   - run in the browser for anonymous users (localStorage activity feeds it).
 *
 * All dates are handled as UTC `YYYY-MM-DD` day keys so a streak never depends on
 * the viewer's timezone or on wall-clock time within a day.
 */

/** A single day of learning activity. */
export type DailyActivity = {
  /** UTC day key, `YYYY-MM-DD`. */
  dateKey: string
  minutesLearned: number
  lessonsCompleted: number
}

export type StreakStats = {
  currentStreak: number
  longestStreak: number
  totalMinutes: number
  totalLessonsCompleted: number
  /** Number of distinct days with any activity. */
  activeDays: number
  /** Whether today (UTC) already has activity - drives the "active today" flame. */
  activeToday: boolean
}

/** Format a millisecond timestamp as a UTC `YYYY-MM-DD` day key. */
export function utcDateKey(timestamp: number): string {
  return new Date(timestamp).toISOString().slice(0, 10)
}

/** Parse a `YYYY-MM-DD` key to the UTC midnight timestamp it denotes. */
function dayKeyToUtcMs(dateKey: string): number {
  // `Date.parse('YYYY-MM-DD')` is spec'd to be UTC midnight.
  return Date.parse(`${dateKey}T00:00:00.000Z`)
}

const DAY_MS = 86_400_000

/** Whole-day difference `a - b` between two UTC day keys (can be negative). */
export function dayDiff(a: string, b: string): number {
  return Math.round((dayKeyToUtcMs(a) - dayKeyToUtcMs(b)) / DAY_MS)
}

/**
 * Compute streak + aggregate stats from a list of daily activity rows.
 *
 * `nowMs` is injected (not read from `Date.now()`) so the result is deterministic
 * and testable, and so the server and client agree on "today".
 *
 * Streak rules:
 *   - The current streak counts back from today, allowing a one-day grace: if
 *     today has no activity yet but yesterday did, the streak is still live
 *     (anchored on yesterday). Two empty days in a row breaks it.
 *   - The longest streak is the longest run of consecutive active days anywhere
 *     in the history.
 *   - Days with zero minutes AND zero lessons do not count as active.
 */
export function computeStreakStats(
  activity: ReadonlyArray<DailyActivity>,
  nowMs: number,
): StreakStats {
  const today = utcDateKey(nowMs)
  // De-duplicate by day (defensive) and keep only days with real activity.
  const byDay = new Map<string, DailyActivity>()
  for (const entry of activity) {
    if (!entry.dateKey) continue
    // Ignore future-dated days (client clock skew / tampering). A day after
    // "today" can never be part of a real streak and would otherwise let a
    // skewed clock anchor a phantom current streak.
    if (dayDiff(entry.dateKey, today) > 0) continue
    const minutes = Math.max(0, entry.minutesLearned)
    const lessons = Math.max(0, entry.lessonsCompleted)
    if (minutes <= 0 && lessons <= 0) continue
    const existing = byDay.get(entry.dateKey)
    byDay.set(entry.dateKey, {
      dateKey: entry.dateKey,
      minutesLearned: (existing?.minutesLearned ?? 0) + minutes,
      lessonsCompleted: (existing?.lessonsCompleted ?? 0) + lessons,
    })
  }

  const days = [...byDay.keys()].sort()
  const totalMinutes = [...byDay.values()].reduce(
    (sum, day) => sum + day.minutesLearned,
    0,
  )
  const totalLessonsCompleted = [...byDay.values()].reduce(
    (sum, day) => sum + day.lessonsCompleted,
    0,
  )

  if (days.length === 0) {
    return {
      currentStreak: 0,
      longestStreak: 0,
      totalMinutes: 0,
      totalLessonsCompleted: 0,
      activeDays: 0,
      activeToday: false,
    }
  }

  // Longest streak: longest run of consecutive day keys.
  let longestStreak = 1
  let run = 1
  for (let i = 1; i < days.length; i += 1) {
    if (dayDiff(days[i], days[i - 1]) === 1) {
      run += 1
    } else {
      run = 1
    }
    if (run > longestStreak) longestStreak = run
  }

  // Current streak: walk back from the most recent active day, but only if that
  // day is today or yesterday (one-day grace). Otherwise the streak is broken.
  const daySet = byDay
  const activeToday = daySet.has(today)
  const lastDay = days[days.length - 1]
  const gapToToday = dayDiff(today, lastDay)

  let currentStreak = 0
  // 0 <= gap <= 1: anchor must be today or yesterday. Future days are already
  // filtered out above, but guard against a negative gap defensively so clock
  // skew can never produce a live streak.
  if (gapToToday >= 0 && gapToToday <= 1) {
    // Anchor on the most recent active day and count consecutive days back.
    let cursor = lastDay
    currentStreak = 1
    for (let i = days.length - 2; i >= 0; i -= 1) {
      if (dayDiff(cursor, days[i]) === 1) {
        currentStreak += 1
        cursor = days[i]
      } else {
        break
      }
    }
  }

  return {
    currentStreak,
    longestStreak,
    totalMinutes,
    totalLessonsCompleted,
    activeDays: days.length,
    activeToday,
  }
}

/** Number of lessons every course in the 5x7 curriculum requires for a certificate. */
export const LESSONS_PER_COURSE = 7

/**
 * Pure certificate-eligibility check.
 *
 * Given the set of completed lesson slugs for a course and the full list of that
 * course's lesson slugs, a certificate is eligible only when every lesson of the
 * course is completed. Returns the completion split so callers can render a
 * "5 / 7 completed" progress hint without re-deriving it.
 */
export function certificateEligibility(input: {
  courseLessonSlugs: ReadonlyArray<string>
  completedLessonSlugs: ReadonlyArray<string>
}): { eligible: boolean; completed: number; total: number } {
  const total = input.courseLessonSlugs.length
  const completedSet = new Set(input.completedLessonSlugs)
  const completed = input.courseLessonSlugs.filter((slug) =>
    completedSet.has(slug),
  ).length
  return {
    eligible: total > 0 && completed >= total,
    completed,
    total,
  }
}
